<template>
  <div
    class="mind-map-preview"
    :class="{ 'is-fullscreen': isFullscreen }"
    :role="isFullscreen ? 'dialog' : null"
    :aria-modal="isFullscreen ? 'true' : null"
    :aria-label="isFullscreen ? `${data.title || '思维导图'}大图预览` : null"
  >
    <div class="mind-map-toolbar" aria-label="思维导图工具栏">
      <button type="button" title="缩小" aria-label="缩小思维导图" @click.stop="zoomOut">
        <i class="el-icon-zoom-out" aria-hidden="true"></i>
      </button>
      <button type="button" title="放大" aria-label="放大思维导图" @click.stop="zoomIn">
        <i class="el-icon-zoom-in" aria-hidden="true"></i>
      </button>
      <button type="button" title="适应画布" aria-label="使思维导图适应画布" @click.stop="fit">
        <i class="el-icon-refresh-right" aria-hidden="true"></i>
      </button>
      <button
        type="button"
        :title="isFullscreen ? '退出大图' : '放大查看'"
        :aria-label="isFullscreen ? '退出思维导图大图' : '放大查看思维导图'"
        @click.stop="toggleFullscreen"
      >
        <i class="el-icon-full-screen" aria-hidden="true"></i>
      </button>
    </div>
    <svg ref="svg" class="mind-map-canvas" role="img" :aria-label="`${data.title || '思维导图'}预览`"></svg>
    <p class="mind-map-hint">
      {{ isFullscreen ? '按 Esc 退出大图；可拖动、缩放或折叠节点' : '可拖动画布、滚轮缩放，点击右上角放大查看' }}
    </p>
  </div>
</template>

<script>
import { Markmap } from 'markmap-view'

export default {
  name: 'MindMapPreview',
  props: {
    data: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      markmap: null,
      resizeObserver: null,
      fitTimer: null,
      isFullscreen: false,
      previousBodyOverflow: '',
      pageScrollLocked: false
    }
  },
  watch: {
    data: {
      deep: true,
      handler () {
        if (this.$refs.svg) this.renderMindMap()
      }
    }
  },
  mounted () {
    this.renderMindMap()
    this.observeSize()
    window.addEventListener('keydown', this.handleWindowKeydown)
  },
  beforeDestroy () {
    clearTimeout(this.fitTimer)
    if (this.resizeObserver) this.resizeObserver.disconnect()
    else window.removeEventListener('resize', this.scheduleFit)
    window.removeEventListener('keydown', this.handleWindowKeydown)
    this.restorePageScroll()
    this.restoreFullscreenHost()
    if (this.markmap) this.markmap.destroy()
  },
  methods: {
    escapeHtml (value) {
      const entities = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }
      return String(value == null ? '' : value).replace(/[&<>"']/g, character => entities[character])
    },
    buildTree () {
      const source = Array.isArray(this.data.nodes) ? this.data.nodes.slice(0, 80) : []
      const rows = []
      const rowMap = new Map()

      source.forEach((node, index) => {
        const id = String((node && node.id) || `node-${index}`)
        if (rowMap.has(id)) return
        const row = {
          id,
          parentId: node && node.parentId != null ? String(node.parentId) : '',
          title: String((node && node.title) || `节点 ${index + 1}`),
          summary: String((node && node.summary) || ''),
          children: []
        }
        rows.push(row)
        rowMap.set(id, row)
      })

      if (!rows.length) return null

      rows.forEach(row => {
        const parent = row.parentId && rowMap.get(row.parentId)
        if (parent && parent.id !== row.id) parent.children.push(row.id)
      })

      const visited = new Set()
      const buildNode = (id, path = new Set()) => {
        if (path.has(id)) return null
        const row = rowMap.get(id)
        if (!row) return null
        visited.add(id)
        const nextPath = new Set(path)
        nextPath.add(id)
        const summary = row.summary ? ` title="${this.escapeHtml(row.summary.slice(0, 500))}"` : ''
        return {
          content: `<span${summary}>${this.escapeHtml(row.title)}</span>`,
          children: row.children.map(childId => buildNode(childId, nextPath)).filter(Boolean),
          payload: { nodeId: row.id }
        }
      }

      const rootIds = rows
        .filter(row => !row.parentId || !rowMap.has(row.parentId) || row.parentId === row.id)
        .map(row => row.id)
      if (!rootIds.length) rootIds.push(rows[0].id)

      const forest = rootIds.map(id => buildNode(id)).filter(Boolean)
      rows.forEach(row => {
        if (!visited.has(row.id)) {
          const node = buildNode(row.id)
          if (node) forest.push(node)
        }
      })

      if (forest.length === 1) return forest[0]
      return {
        content: `<span>${this.escapeHtml(this.data.title || '思维导图')}</span>`,
        children: forest
      }
    },
    async renderMindMap () {
      const tree = this.buildTree()
      if (!tree || !this.$refs.svg) return
      if (!this.markmap) {
        this.markmap = Markmap.create(this.$refs.svg, {
          autoFit: false,
          duration: 250,
          fitRatio: 0.9,
          initialExpandLevel: -1,
          maxInitialScale: 1.4,
          maxWidth: 190,
          nodeMinHeight: 20,
          paddingX: 10,
          pan: true,
          scrollForPan: false,
          spacingHorizontal: 72,
          spacingVertical: 8,
          zoom: true
        }, tree)
      } else {
        await this.markmap.setData(tree)
      }
      this.scheduleFit()
    },
    observeSize () {
      if (typeof ResizeObserver !== 'undefined') {
        this.resizeObserver = new ResizeObserver(() => this.scheduleFit())
        this.resizeObserver.observe(this.$el)
      } else {
        window.addEventListener('resize', this.scheduleFit)
      }
    },
    scheduleFit () {
      clearTimeout(this.fitTimer)
      this.fitTimer = setTimeout(() => this.fit(), 80)
    },
    fit () {
      if (this.markmap) this.markmap.fit(1.4)
    },
    zoomIn () {
      if (this.markmap) this.markmap.rescale(1.25)
    },
    zoomOut () {
      if (this.markmap) this.markmap.rescale(0.8)
    },
    async toggleFullscreen () {
      this.isFullscreen = !this.isFullscreen
      if (this.isFullscreen) {
        this.previousBodyOverflow = document.body.style.overflow
        this.pageScrollLocked = true
        document.body.style.overflow = 'hidden'
        this.promoteFullscreenHost()
      } else {
        this.restorePageScroll()
        this.restoreFullscreenHost()
      }
      await this.$nextTick()
      this.scheduleFit()
    },
    handleWindowKeydown (event) {
      if (event.key !== 'Escape' || !this.isFullscreen) return
      this.toggleFullscreen()
    },
    restorePageScroll () {
      if (!this.pageScrollLocked) return
      document.body.style.overflow = this.previousBodyOverflow
      this.pageScrollLocked = false
    },
    promoteFullscreenHost () {
      const host = this.$el.closest('.material-extract-body')
      if (!host) return
      this._fullscreenHost = host
      this._previousHostZIndex = host.style.zIndex
      host.style.zIndex = '10001'
    },
    restoreFullscreenHost () {
      if (!this._fullscreenHost) return
      this._fullscreenHost.style.zIndex = this._previousHostZIndex || ''
      this._fullscreenHost = null
      this._previousHostZIndex = ''
    }
  }
}
</script>

<style scoped>
.mind-map-preview {
  position: relative;
  min-height: 420px;
  margin-top: 13px;
  overflow: hidden;
  border: 1px solid #e4ebe8;
  border-radius: 8px;
  background: #fff;
}

.mind-map-preview.is-fullscreen {
  position: fixed;
  z-index: 10000;
  inset: 16px;
  min-height: 0;
  margin: 0;
  border-color: #cfe5dc;
  border-radius: 12px;
  box-shadow: 0 0 0 100vmax rgba(15, 23, 42, .48), 0 20px 60px rgba(15, 23, 42, .24);
}

.mind-map-canvas {
  display: block;
  width: 100%;
  height: 420px;
  cursor: grab;
}

.mind-map-canvas:active { cursor: grabbing; }

.is-fullscreen .mind-map-canvas {
  height: calc(100vh - 32px);
}

.mind-map-toolbar {
  position: absolute;
  z-index: 2;
  top: 10px;
  right: 10px;
  display: flex;
  gap: 5px;
  padding: 4px;
  border: 1px solid #dfe8e4;
  border-radius: 7px;
  background: rgba(255, 255, 255, .94);
  box-shadow: 0 2px 8px rgba(32, 67, 55, .08);
}

.mind-map-toolbar button {
  width: 28px;
  height: 28px;
  padding: 0;
  border: 0;
  border-radius: 5px;
  background: transparent;
  color: #52645e;
  cursor: pointer;
}

.mind-map-toolbar button:hover,
.mind-map-toolbar button:focus-visible {
  outline: none;
  background: #eaf8f3;
  color: #13a872;
}

.mind-map-hint {
  position: absolute;
  right: 12px;
  bottom: 9px;
  margin: 0;
  color: #9aa6a1;
  font-size: 11px;
  pointer-events: none;
}
</style>
